/**
 * Prüfung eines Vertrags vor dem Versand. Liefert Befunde (Finding) und blockiert
 * nie selbst: `error` sperrt den Versand, `warning` und `hint` nicht. Die Prüfung
 * rendert immer in der Agentursicht (ohne Maskierung, mit Anlage), damit nichts
 * durch eine eingeschränkte Sicht verborgen bleibt; die Anlage wird mitgeprüft.
 */
import { renderContract, sampleNotices, scanPlaceholders, type Placeholder, type RenderContractInput } from "./contract-render.js";
import { contractPrintProblem } from "./contract-pdf.js";
import { minorStatus } from "./contract-term.js";
import { CONTRACT_FILTERS } from "./contract-render.js";
import { CONTRACT_ROLES, contextValue, isContractRole, isSystemVariablePath, type ContractRole } from "./contract-variables.js";
import { looksGerman, textLanguage } from "./contract-locale.js";
import { CONTRACT_LIMITS, CONTRACT_WS_CLASS, contractCharProblem, contractMarkupProblem, contractRefProblem } from "./contract-markup.js";
import type { Finding, Language } from "./types.js";

export type ContractCheckCode =
  | "UNKNOWN_PLACEHOLDER"
  | "MISSING_REQUIRED"
  | "UNRESOLVED"
  | "GAP_TEXT"
  | "MISSING_SIGNATURE_BLOCK"
  | "DUPLICATE_SIGNATURE_BLOCK"
  | "LANGUAGE_MISMATCH"
  | "EMPTY_BODY"
  | "FILLABLE_OPEN"
  | "INVALID_VARIABLE_KEY"
  | "FILLABLE_ROLE_NOT_SIGNER"
  | "UNREQUESTED_SIGNATURE_BLOCK"
  | "MASTER_DATA_MISMATCH"
  | "LEGAL_TEXT_LANGUAGE"
  | "SAMPLE_NOTICE"
  | "TEXT_AFTER_SIGNATURE"
  /** Unterschriftsfeld im vertraulichen Teil (Prüfung 0–2, Befund 5; wie contract_send) */
  | "SIGNATURE_IN_PRIVATE_PART"
  | "MINOR_WITHOUT_GUARDIAN"
  | "MINOR_ONE_GUARDIAN"
  | "MINOR_TURNS_18"
  | "MINOR_UNDER_15"
  | "TERM_NOT_STRUCTURED"
  | "DATE_IN_PAST"
  | "USAGE_RIGHTS_CONFLICT"
  | "USAGE_RIGHTS_FREE_TEXT"
  /** Auszeichnung außerhalb der Positivliste (VB-25, contract-markup.ts); dieselbe Regel sperrt Aktivieren und Versand im Server */
  | "MARKUP_NOT_ALLOWED"
  /** K-532: eingefügter Freitext (Kooperation oder eigene Angabe) in anderer Sprache als der Vertrag */
  | "FOREIGN_TEXT";

/** Schlüssel eigener Variablen: wie in der Datenbank (contract_public_fill), sonst trifft das Ausfüllen den Platzhalter nicht (R-28). */
export const VARIABLE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** Ersatz für Platzhalter bei der Lückentext-Suche im Quelltext: „[{{brand.name}}]“ ist kein Lückentext (R-20). */
const PLACEHOLDER_MARK = "\u2063";

export interface ContractCheckInput extends RenderContractInput {
  templateLanguage: Language;
  recipientLanguage?: Language | null;
  requiredSigners: ContractRole[];
  /**
   * Eingetragene Unterzeichner (ohne Agentur). Mit ihnen prüft die Prüfung Minderjährige: Ist der Creator zu Vertragsbeginn
   * unter 18 (context.talent.birth_date), braucht es eine gesetzliche Vertretung (Befund 2). Ohne Angabe entfällt diese Prüfung.
   */
  signers?: readonly { role: ContractRole; capacity: "party" | "legal_guardian" }[];
}

export type ContractPart = "body" | "annex";

/** Lückentext-Muster: Platzhalter aus Word-Vorlagen, Datums-Muster, Linien, Blindtext, TODO. */
export const GAP_TEXT_PATTERNS: readonly RegExp[] = [
  /\[[^\[\]\n]{1,80}\]/g,
  /XX\.XX\.XXXX/g,
  /TT\.MM\.JJJJ/g,
  /DD\.MM\.YYYY/g,
  /_{3,}/g,
  /Lorem ipsum/gi,
  /\bTODO\b/g,
  // Grundgerüst neuer Vorlagen (Befund 17)
  /Beschreibe hier[^.<]*\.?/g,
  /\bVertragstitel\b/g,
  /Describe (?:here|the subject)[^.<]*\.?/g,
];

function stripTags(html: string): string {
  return html
    .replace(/\{\{[^{}]*\}\}/g, PLACEHOLDER_MARK)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function gapTexts(html: string): string[] {
  const text = stripTags(html);
  const found = new Set<string>();
  for (const re of GAP_TEXT_PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) if (!m[0].includes(PLACEHOLDER_MARK)) found.add(m[0]);
  }
  return [...found];
}

/** Systemdaten, die eine Frist oder ein Ende sind: liegen sie beim Versand schon zurück, fragt die Prüfung nach (R2-09). */
const PAST_CHECK_PATHS = new Set(["deal.content_deadline", "deal.usage_rights_until", "deal.exclusive_until", "contract.ends_on"]);

/**
 * Unterschriftsfeld als Markup in jeder Schreibweise der Positivliste (Attributreihenfolge, Leerraum im Tag); gleich
 * app.contract_signature_rx (0116).
 */
export const SIGNATURE_DIV_PATTERN = '<div(?:[ \\t\\r\\n\\f]+[a-z][a-z0-9-]*="[^"<>]*")*[ \\t\\r\\n\\f]+class="tk-signature"(?:[ \\t\\r\\n\\f]+[a-z][a-z0-9-]*="[^"<>]*")*[ \\t\\r\\n\\f]*>';
const SIGNATURE_DIV_RX = () => new RegExp(SIGNATURE_DIV_PATTERN, "g");

/** Sichtbare Zeichen einer Beschriftung: ohne Tags, &nbsp; (auch als &#160; bzw. &#xA0;) und Leerraum; gleich app.contract_label_rest. */
export function contractLabelRest(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;|&#0*160(?![0-9]);?|&#[xX]0*[aA]0(?![0-9a-fA-F]);?/g, "").replace(new RegExp(CONTRACT_WS_CLASS, "g"), "");
}

/**
 * Text bei den Unterschriftsfeldern (Befund 17; Prüfung 0–2, Befund 4; Runde 2, R2-P2), gleich app.contract_signature_tail_problem:
 * `after` = nach dem letzten Feld steht noch Text (von keiner späteren Unterschrift gedeckt), `label` = zwischen zwei Feldern
 * steht mehr als eine kurze Beschriftung (höchstens CONTRACT_LIMITS.signatureLabel sichtbare Zeichen ohne Leerraum je Lücke).
 */
export function contractSignatureTailProblem(html: string): "after" | "label" | null {
  const sigs = [...html.matchAll(SIGNATURE_DIV_RX())];
  if (!sigs.length) return null;
  const endOf = (m: RegExpMatchArray) => m.index! + m[0].length;
  if (contractLabelRest(html.slice(endOf(sigs[sigs.length - 1]!))) !== "") return "after";
  for (let i = 0; i + 1 < sigs.length; i++) {
    if (Array.from(contractLabelRest(html.slice(endOf(sigs[i]!), sigs[i + 1]!.index))).length > CONTRACT_LIMITS.signatureLabel) return "label";
  }
  return null;
}

/** Sperrt einer der Befunde den Versand? */
export function contractSendBlocked(findings: readonly Finding[]): boolean {
  return findings.some((f) => f.severity === "error");
}

export function checkContract(input: ContractCheckInput): Finding[] {
  const findings: Finding[] = [];
  const add = (code: ContractCheckCode, severity: Finding["severity"], params?: Record<string, string | number>) => {
    findings.push(params ? { code, severity, params } : { code, severity });
  };

  const body = input.bodyHtml ?? "";
  const annex = input.annexHtml ?? "";
  const parts: { part: ContractPart; source: string }[] = [{ part: "body", source: body }];
  if (annex !== "") parts.push({ part: "annex", source: annex });

  if (stripTags(body).replaceAll(PLACEHOLDER_MARK, "") === "" && scanPlaceholders(body).length === 0) add("EMPTY_BODY", "error");

  // Positivliste (VB-25): was nicht angezeigt oder anders gedruckt würde, sperrt wie im Server (contract_send, Aktivieren);
  // dazu Zeichenreferenzen, die Browser und PDF verschieden zeigen (Prüfung 0–2, Befunde 15 und 16)
  const markupParts = new Set<ContractPart>();
  for (const { part, source } of parts) {
    const problem = contractMarkupProblem(source) ?? contractRefProblem(source);
    if (problem) { add("MARKUP_NOT_ALLOWED", "error", { part, problem }); markupParts.add(part); }
  }

  for (const d of input.customVars) {
    if (!VARIABLE_KEY_PATTERN.test(d.key)) add("INVALID_VARIABLE_KEY", "error", { variable: d.key });
  }

  const defs = new Map(input.customVars.map((d) => [d.key, d]));
  const knownFilters = CONTRACT_FILTERS as readonly string[];
  const unknownPaths = new Set<string>();
  const referencedVars = new Set<string>();

  const placeholders: { part: ContractPart; p: Placeholder }[] = [];
  for (const { part, source } of parts) for (const p of scanPlaceholders(source)) placeholders.push({ part, p });

  for (const { part, p } of placeholders) {
    if (p.kind === "signature") {
      if (isContractRole(p.path)) continue;
      if (!unknownPaths.has(`signature:${p.path}`)) {
        unknownPaths.add(`signature:${p.path}`);
        add("UNKNOWN_PLACEHOLDER", "error", { path: `signature:${p.path}`, part });
      }
      continue;
    }
    if (p.kind !== "var" && p.kind !== "if") continue;
    const isVar = p.path.startsWith("var.");
    const key = p.path.slice(4);
    const known = isVar ? defs.has(key) : isSystemVariablePath(p.path);
    if (!known) {
      if (!unknownPaths.has(p.path)) {
        unknownPaths.add(p.path);
        add("UNKNOWN_PLACEHOLDER", "error", { path: p.path, part });
      }
      continue;
    }
    if (isVar) referencedVars.add(key);
    for (const f of p.filters) {
      if (!knownFilters.includes(f)) {
        const id = `${p.path}|${f}`;
        if (!unknownPaths.has(id)) {
          unknownPaths.add(id);
          add("UNKNOWN_PLACEHOLDER", "error", { path: p.path, filter: f, part });
        }
      }
    }
  }

  // Ausfüllfeld im Text, dessen Rolle gar nicht unterschreibt: der Platzhalter bliebe für immer leer (R-28)
  for (const d of input.customVars) {
    if (d.fillable_by && referencedVars.has(d.key) && !input.requiredSigners.includes(d.fillable_by)) {
      add("FILLABLE_ROLE_NOT_SIGNER", "error", { variable: d.key, fillable_by: d.fillable_by });
    }
  }

  // Rendern in der Agentursicht: Rest von {{ und fehlende Systemwerte
  const rendered = renderContract({
    ...input,
    view: "agency",
    annexHiddenFrom: [],
    maskAgencyAddressUntilComplete: false,
    allSigned: true,
  });
  // Angaben im gewählten Zweig: steht eine Pflichtangabe nur im nicht gewählten {{else}}-Zweig, fehlt sie nicht (VERTRAEGE-R3-07)
  const activeVars = new Set(rendered.used.filter((p) => p.startsWith("var.")).map((p) => p.slice(4)));

  // Pflichtvariablen ohne Wert (nicht fillable) und offene fillable Variablen
  const missingRequired = new Set<string>();
  const empty = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
  for (const def of input.customVars) {
    const hasValue = !empty(input.values?.[def.key]) || !empty(def.default);
    if (hasValue) continue;
    if (def.fillable_by) {
      if (referencedVars.has(def.key)) add("FILLABLE_OPEN", "hint", { variable: def.key, fillable_by: def.fillable_by });
      continue;
    }
    if (def.required) {
      if (referencedVars.has(def.key) && !activeVars.has(def.key)) continue;
      missingRequired.add(def.key);
      add("MISSING_REQUIRED", "error", { variable: def.key });
    }
  }

  const reported = new Set<string>();
  for (const u of rendered.unresolved) {
    if (unknownPaths.has(u)) continue;
    if (u.startsWith("var.") && missingRequired.has(u.slice(4))) continue;
    if (!reported.has(u)) {
      reported.add(u);
      add("UNRESOLVED", "error", { path: u });
    }
  }
  const leftover = /\{\{[^{}]*\}\}/g;
  for (const out of [rendered.html, rendered.annexHtml ?? ""]) {
    let m: RegExpExecArray | null;
    leftover.lastIndex = 0;
    while ((m = leftover.exec(out)) !== null) {
      const inner = m[0].slice(2, -2).trim();
      const id = inner.replace(/\s*\|\s*/g, "|");
      if (unknownPaths.has(id) || reported.has(id) || (id.startsWith("var.") && missingRequired.has(id.slice(4)))) continue;
      if (id.startsWith("#if ") && (unknownPaths.has(id.slice(4).trim()) || reported.has(id))) continue;
      reported.add(id);
      add("UNRESOLVED", "error", { path: id });
    }
    // Wie contract_send: jedes verbliebene „{{“ sperrt, auch ohne schließendes „}}“ (R-18)
    const rest = out.replace(leftover, "");
    const at = rest.indexOf("{{");
    if (at >= 0) {
      const fragment = rest.slice(at, at + 40).replace(/<[^>]*>/g, "");
      if (!reported.has(fragment)) {
        reported.add(fragment);
        add("UNRESOLVED", "error", { path: fragment });
      }
    }
  }

  // Lückentext
  for (const { part, source } of parts) for (const g of gapTexts(source)) add("GAP_TEXT", "error", { text: g, part });

  // Steuer- und unsichtbare Zeichen aus eingesetzten Werten (die Quelle ist oben geprüft): sperrt wie contract_send (Befund 3)
  for (const [part, out] of [["body", rendered.html], ["annex", rendered.annexHtml ?? ""]] as [ContractPart, string][]) {
    const problem = markupParts.has(part) ? null : contractCharProblem(out) ?? contractPrintProblem(out);
    if (problem) add("MARKUP_NOT_ALLOWED", "error", { part, problem });
  }

  // Signaturblöcke: gezählt im gerenderten Hauptteil, in jeder Schreibweise des Felds, genau wie contract_send (R-19, Befund 4);
  // im vertraulichen Teil sperrt jedes Unterschriftsfeld (Befund 5)
  const signatureCount = new Map<ContractRole, number>();
  for (const m of rendered.html.matchAll(SIGNATURE_DIV_RX())) {
    const role = /data-role="([a-z]+)"/.exec(m[0])?.[1] ?? "";
    if (isContractRole(role)) signatureCount.set(role, (signatureCount.get(role) ?? 0) + 1);
  }
  if (SIGNATURE_DIV_RX().test(rendered.annexHtml ?? "")) add("SIGNATURE_IN_PRIVATE_PART", "error");
  for (const role of input.requiredSigners) {
    if ((signatureCount.get(role) ?? 0) === 0) add("MISSING_SIGNATURE_BLOCK", "error", { role });
  }
  for (const role of CONTRACT_ROLES) {
    const n = signatureCount.get(role) ?? 0;
    if (n > 1) add("DUPLICATE_SIGNATURE_BLOCK", "error", { role, count: n });
    // Unterschriftsfeld einer Partei, die nicht unterschreibt: das Feld bliebe für immer offen (wie contract_send, Befund 3)
    if (n > 0 && !input.requiredSigners.includes(role)) add("UNREQUESTED_SIGNATURE_BLOCK", "error", { role });
  }

  // Eigene Angaben, die aus den Stammdaten vorbelegt werden: abweichender Wert ist ein Hinweis (Befund 17)
  for (const def of input.customVars) {
    if (!def.prefill || !referencedVars.has(def.key)) continue;
    const own = input.values?.[def.key];
    const master = contextValue(input.context, def.prefill);
    if (empty(own) || empty(master)) continue;
    const same = typeof master === "number" || typeof own === "number" ? Number(own) === Number(master) : String(own).trim() === String(master).trim();
    if (!same) add("MASTER_DATA_MISMATCH", "warning", { variable: def.key, path: def.prefill, expected: String(master) });
  }

  // Englischer Vertrag mit deutschen Firmenangaben (Rechtswahl, Gerichtsstand, Funktion; Befund 8)
  if (input.templateLanguage === "en") {
    const used = new Set(placeholders.filter((x) => x.p.kind === "var").map((x) => x.p.path));
    for (const path of ["agency.governing_law", "agency.jurisdiction", "agency.representative_position"]) {
      const v = contextValue(input.context, path);
      if (used.has(path) && typeof v === "string" && looksGerman(v)) add("LEGAL_TEXT_LANGUAGE", "warning", { path, value: v });
    }
  }

  // K-532: eingefügte Freitexte in anderer Sprache als der Vertrag (z. B. englische Nutzungsrechte aus der Kooperation im
  // deutschen Vertrag); nur Text aus der Kooperation und eigene Textangaben, einmal je Angabe
  const sprachGeprueft = new Set<string>();
  for (const { p } of placeholders) {
    if (p.kind !== "var" || sprachGeprueft.has(p.path)) continue;
    sprachGeprueft.add(p.path);
    let wert: unknown;
    if (p.path.startsWith("var.")) {
      const d = defs.get(p.path.slice(4));
      if (!d || (d.type !== "text" && d.type !== "lines")) continue;
      wert = input.values?.[d.key] ?? d.default;
    } else {
      if (!p.path.startsWith("deal.")) continue;
      wert = contextValue(input.context, p.path);
    }
    if (typeof wert !== "string") continue;
    const sprache = textLanguage(wert);
    if (sprache && sprache !== input.templateLanguage) add("FOREIGN_TEXT", "warning", { path: p.path, language: sprache, sample: wert.trim().replace(/\s+/g, " ").slice(0, 80) });
  }

  // Sprache
  if (input.recipientLanguage && input.recipientLanguage !== input.templateLanguage) {
    add("LANGUAGE_MISMATCH", "warning", { template: input.templateLanguage, recipient: input.recipientLanguage });
  }

  // Hinweis im Vorlagenkopf (Muster): steht nicht im Vertrag, soll aber gesehen werden (Befund 5)
  if (sampleNotices(body).length || sampleNotices(annex).length) add("SAMPLE_NOTICE", "hint");

  // Text nach dem ersten Unterschriftsfeld ist nicht von allen Unterschriften gedeckt (Befund 17; Prüfung 0–2, Befund 4: jede
  // Schreibweise des Felds, auch Text zwischen den Feldern; wie contract_send)
  // Runde 2, R2-P2: kurze Beschriftungen zwischen den Feldern sind erlaubt („Für die Marke:“, „Ort, Datum“), nach dem letzten nichts
  if (contractSignatureTailProblem(rendered.html)) add("TEXT_AFTER_SIGNATURE", "error");

  // Minderjährige (Befund 2): Alter am Tag der Unterschrift (VERTRAEGE-R2-01: frühestens heute, wie contract_send)
  const birth = contextValue(input.context, "talent.birth_date");
  const today = contextValue(input.context, "dates.today") as string | null | undefined;
  if (typeof birth === "string" && input.requiredSigners.includes("talent")) {
    const start = (contextValue(input.context, "contract.starts_on") ?? contextValue(input.context, "contract.date") ?? today) as string | null;
    const end = contextValue(input.context, "contract.ends_on") as string | null | undefined;
    if (start) {
      const st = minorStatus(birth, start, end ?? null, today ?? null);
      if (st.minor) {
        const guardians = (input.signers ?? []).filter((x) => x.role === "talent" && x.capacity === "legal_guardian").length;
        if (input.signers && guardians === 0) add("MINOR_WITHOUT_GUARDIAN", "error", { age: st.age ?? 0 });
        if (input.signers && guardians === 1) add("MINOR_ONE_GUARDIAN", "hint");
        if (st.turns18During) add("MINOR_TURNS_18", "warning", { date: st.turns18During });
        if (st.under15) add("MINOR_UNDER_15", "hint", { age: st.age ?? 0 });
      }
    }
  }

  // Fristen, die vor dem Versand schon verstrichen sind (VERTRAEGE-R2-09): etwa „veröffentlicht bis spätestens …“. Gezählt
  // werden Datumsangaben im Text, die eine Frist oder ein Ende sind; Vertragsdatum, Beginn und Geburtsdatum nicht.
  if (typeof today === "string" && /^\d{4}-\d{2}-\d{2}/.test(today)) {
    const reportedPast = new Set<string>();
    for (const { p } of placeholders) {
      if (p.kind !== "var" || reportedPast.has(p.path)) continue;
      const isVar = p.path.startsWith("var.");
      const dateLike = isVar ? defs.get(p.path.slice(4))?.type === "date" : PAST_CHECK_PATHS.has(p.path);
      if (!dateLike) continue;
      const v = isVar ? (input.values?.[p.path.slice(4)] ?? defs.get(p.path.slice(4))?.default) : contextValue(input.context, p.path);
      if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}/.test(v) || v.slice(0, 10) >= today.slice(0, 10)) continue;
      reportedPast.add(p.path);
      add("DATE_IN_PAST", "warning", { path: p.path, date: v.slice(0, 10) });
    }
  }

  // Nutzungsrechte (VERTRAEGE-R3-07): Haben die Beiträge der Kooperation eine Dauer, darf der Vertrag keine andere nennen.
  // Gezählt wird nur, was im gewählten Zweig steht (die neue Musterfassung nennt dann nur die Beiträge).
  if (contextValue(input.context, "deal.usage_rights_by_piece") === true) {
    const used = new Set(rendered.used);
    const pieces = contextValue(input.context, "deal.usage_rights_months");
    if (used.has("var.usage_rights_months")) {
      const own = input.values?.["usage_rights_months"] ?? defs.get("usage_rights_months")?.default;
      if (!empty(own) && (typeof pieces !== "number" || Number(own) !== pieces)) {
        add("USAGE_RIGHTS_CONFLICT", "warning", { value: String(own), ...(typeof pieces === "number" ? { pieces } : {}) });
      }
    }
    if (used.has("deal.usage_rights") && !used.has("deal.usage_rights_list") && !empty(contextValue(input.context, "deal.usage_rights"))) {
      add("USAGE_RIGHTS_FREE_TEXT", "hint");
    }
  }

  // Laufzeit im Text, aber keine Felder: Erinnerung und Verlängerung liefen ins Leere (Befund 10)
  const usesTermFields = placeholders.some((x) => /^contract\.(term_months|ends_on|starts_on|notice_days|notice_period|auto_renew|renewal_months)$/.test(x.p.path));
  if (!usesTermFields) {
    const fields = ["contract.term_months", "contract.ends_on", "contract.notice_days"].map((p) => contextValue(input.context, p));
    const mentions = /Laufzeit von|Kündigungsfrist|verlängert sich|term of \d|notice period|is renewed/i.test(stripTags(rendered.html))
      || [...referencedVars].some((k) => /^(term|notice|laufzeit|kuendigung|renewal)/.test(k));
    if (mentions && fields.every((v) => v === null || v === undefined || v === "")) add("TERM_NOT_STRUCTURED", "warning");
  }

  return findings;
}

// Vertragsbaukasten (Schritt 2): Bausteine, Übersetzer, Umwandlung des Bestands, Prüfung wie der Server (0112).
// Über diese Einstiegsstelle, weil keins der Module contract-check.ts einbindet (keine Kreise).
export * from "./contract-blocks.js";
export * from "./contract-blocks-compile.js";
export * from "./contract-blocks-convert.js";
export * from "./contract-blocks-check.js";
// Bindung des gerenderten Texts an seine Quelle (Prüfung 0–2, Befund 1; gleich app.contract_source_pattern, 0115)
export * from "./contract-source-match.js";
